import { ColumnProps } from "@monolith-forensics/monolith-ui";
import { Link } from "react-router-dom";
import styled from "styled-components";

import PassIndicator from "@/components/PassIndicator";
import { monolithMoment } from "@/utils/date-format";

import { AuditLog } from "../audits.query";

const LinkCell = styled.div`
  .label {
    font-weight: 500;
    color: ${(props) => props.theme.palette.primary.main};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
      color: ${(props) => props.theme.palette.text.primary};
    }
  }
`;

const AuditNameCell = ({ rowData }: { rowData: AuditLog }) => {
  if (!rowData.audit?.uuid) return <>{rowData.audit?.name}</>;

  return (
    <LinkCell>
      <Link to={`/audits/${rowData.audit.uuid}/audit-items`}>
        <div className="label" title="View Audit">
          {rowData.audit.name}
        </div>
      </Link>
    </LinkCell>
  );
};

const AuditItemCell = ({ rowData }: { rowData: AuditLog }) => {
  const href =
    rowData.object_type === "evidence"
      ? `/evidence/${rowData.object_uuid}/overview`
      : `/storage/${rowData.object_uuid}`;

  return (
    <LinkCell>
      <Link to={href}>
        <div className="label">{rowData.object_name}</div>
      </Link>
    </LinkCell>
  );
};

// Columns for the object-scoped Audit Logs view (a storage/evidence item's
// "Audit Logs" tab). Sorting is opted in per column, only on the fields the
// server whitelists (created_on, object_name, created_by, passed_audit) —
// the Table must NOT set table-level enableSorting, since monolith-ui ORs it
// with the per-column flag and a non-whitelisted sort field makes the server
// drop its ORDER BY entirely (see SORTABLE_FIELDS in audits.query.ts).
export const AuditLogColumnDefinitions: ColumnProps[] = [
  {
    dataField: "audit_name",
    caption: "Audit",
    width: 200,
    render: ({ rowData }: { rowData: AuditLog }) => (
      <AuditNameCell rowData={rowData} />
    ),
  },
  {
    dataField: "created_on",
    caption: "Timestamp",
    width: 200,
    dataType: "datetime",
    enableSorting: true,
    render: ({ rowData }: { rowData: AuditLog }) =>
      monolithMoment({
        timestamp: rowData.created_on,
        includeTime: true,
        includeZone: true,
      }),
  },
  {
    dataField: "object_name",
    caption: "Audit Item",
    width: 200,
    visible: false,
    enableSorting: true,
    render: ({ rowData }: { rowData: AuditLog }) => (
      <AuditItemCell rowData={rowData} />
    ),
  },
  {
    dataField: "created_by",
    caption: "User",
    width: 200,
    enableSorting: true,
    render: ({ rowData }: { rowData: AuditLog }) =>
      rowData.created_by?.full_name,
  },
  {
    dataField: "passed_audit",
    caption: "Audit Status",
    width: 200,
    enableSorting: true,
    render: ({ rowData }: { rowData: AuditLog }) => (
      <PassIndicator passed={rowData.passed_audit} />
    ),
  },
  {
    dataField: "notes",
    caption: "Notes",
  },
];

export default AuditLogColumnDefinitions;
